const resolveApiUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL?.trim();
  const ensureApiSuffix = (value = '') => {
    const trimmed = String(value || '').replace(/\/+$/, '');
    if (!trimmed) return '';
    return /\/api$/i.test(trimmed) ? trimmed : `${trimmed}/api`;
  };

  if (envUrl) {
    return ensureApiSuffix(envUrl);
  }

  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }

  return 'http://localhost:5001/api';
};

const API_URL = resolveApiUrl();
const LIVE_MATCHES_UNAVAILABLE_MESSAGE = 'Canli mac verisi su anda alinamadi. Sistem kisa sure icinde tekrar deneyecek.';

const resolveApiOrigin = () => {
  try {
    return new URL(
      API_URL,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5001'
    ).origin;
  } catch (error) {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }

    return 'http://localhost:5001';
  }
};

const API_ORIGIN = resolveApiOrigin();

const buildRequestUrl = (endpoint = '') => {
  const rawEndpoint = String(endpoint || '');

  if (/^https?:\/\//i.test(rawEndpoint)) {
    return rawEndpoint;
  }

  const normalizedEndpoint = rawEndpoint.startsWith('/')
    ? rawEndpoint
    : `/${rawEndpoint}`;

  if (normalizedEndpoint.startsWith('/api/')) {
    return new URL(normalizedEndpoint, API_ORIGIN).toString();
  }

  return `${API_URL}${normalizedEndpoint}`;
};

const getRequestLabel = (requestUrl = '') => {
  try {
    return new URL(
      requestUrl,
      typeof window !== 'undefined' ? window.location.origin : API_ORIGIN
    ).pathname;
  } catch (error) {
    return String(requestUrl || '');
  }
};

const getToken = () => {
  try {
    return window.localStorage.getItem('token');
  } catch (error) {
    return null;
  }
};
const isLiveMatchesEndpoint = (endpoint = '') => String(endpoint || '').startsWith('/matches/live');
const buildLiveMatchesError = ({
  response = null,
  raw = '',
  data = null,
  errorCode = 'LIVE_API_NON_JSON_RESPONSE'
} = {}) => {
  const error = new Error(data?.message || LIVE_MATCHES_UNAVAILABLE_MESSAGE);
  error.errorCode = data?.errorCode || errorCode;
  error.providerStatus = data?.debug?.providerStatus ?? response?.status ?? null;
  error.preview = data?.debug?.preview || String(raw || '').slice(0, 500);
  return error;
};

const api = {
  async request(endpoint, options = {}) {
    const token = getToken();
    let response;
    const requestUrl = buildRequestUrl(endpoint);
    const requestLabel = getRequestLabel(requestUrl);

    try {
      response = await fetch(requestUrl, {
        ...options,
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers
        }
      });
    } catch (error) {
      if (isLiveMatchesEndpoint(endpoint)) {
        throw buildLiveMatchesError({ errorCode: 'LIVE_API_FETCH_FAILED' });
      }
      throw error;
    }

    const raw = await response.text();
    let data = null;

    try {
      data = raw ? JSON.parse(raw) : null;
    } catch (error) {
      if (isLiveMatchesEndpoint(endpoint)) {
        throw buildLiveMatchesError({ response, raw });
      }
      if (!response.ok) {
        const requestError = new Error(`${requestLabel}: Sunucudan JSON yerine HTML/hata sayfasi dondu`);
        requestError.status = response.status;
        requestError.endpoint = endpoint;
        throw requestError;
      }
      const requestError = new Error(`${requestLabel}: Sunucu gecersiz veri dondurdu`);
      requestError.status = response.status;
      requestError.endpoint = endpoint;
      throw requestError;
    }
    
    if (!response.ok) {
      if (isLiveMatchesEndpoint(endpoint)) {
        throw buildLiveMatchesError({
          response,
          raw,
          data,
          errorCode: data?.errorCode || 'LIVE_API_REQUEST_FAILED'
        });
      }
      const error = new Error(data?.message || data?.lastError || data?.error || 'API Error');
      error.status = response.status;
      error.payload = data;
      error.endpoint = endpoint;
      throw error;
    }
    
    return data;
  },

  // Auth
  login: (email, password) => api.request('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  }),

  register: (name, email, password) => api.request('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ name, email, password })
  }),

  getMe: () => api.request('/api/auth/me'),
  updateMe: (payload) => api.request('/api/auth/me', {
    method: 'PUT',
    body: JSON.stringify(payload)
  }),
  changePassword: (currentPassword, newPassword) => api.request('/api/auth/change-password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword })
  }),
  requestPremiumUpgrade: () => api.request('/api/auth/premium-request', {
    method: 'POST'
  }),
  requestAccountDeletion: () => api.request('/api/auth/account-deletion-request', {
    method: 'POST'
  }),

  // Matches
  getLiveMatches: (sync = false, limit = 50, favoriteFixtureIds = []) => {
    const favoriteParam = Array.isArray(favoriteFixtureIds) && favoriteFixtureIds.length
      ? `&favoriteFixtureIds=${encodeURIComponent(favoriteFixtureIds.join(','))}`
      : '';
    return api.request(`/matches/live?limit=${limit}${sync ? '&sync=true' : ''}${favoriteParam}`);
  },
  getRecentLiveSignalEvents: () => api.request('/live-signal-events/recent'),
  getTodayMatches: (sync = false, limit = 75) => api.request(`/matches/today?limit=${limit}${sync ? '&sync=true' : ''}`),
  getUpcomingMatches: (limit = 12) => api.request(`/matches/upcoming?limit=${limit}`),
  getMatches: (params = '') => api.request(`/matches${params ? `?${params}` : ''}`),
  syncTodayMatches: async (date) => {
    try {
      return await api.request('/matches/sync/today', {
        method: 'POST',
        body: JSON.stringify({ date })
      });
    } catch (error) {
      return api.request(`/matches?date=${encodeURIComponent(date)}&limit=100&sort=popular&sync=true`);
    }
  },
  syncLiveMatches: async () => {
    try {
      return await api.request('/matches/sync/live', {
        method: 'POST'
      });
    } catch (error) {
      return api.request('/matches/live?limit=100&sync=true');
    }
  },

  // Analysis
  getAllMatchesAnalysis: () => api.request('/all-matches-analysis'),
  getDailyAiCards: (dateKey = '') => {
    const params = new URLSearchParams();
    if (dateKey) {
      params.set('date', dateKey);
      params.set('dateKey', dateKey);
    }
    params.set('limit', '200');
    params.set('days', '3');
    params.set('includeWindow', 'true');
    params.set('_', Date.now().toString());
    return api.request(`/daily-ai-cards?${params.toString()}`);
  },
  getAiAnalysisPublished: (dateKey = '') => {
    const params = new URLSearchParams();
    if (dateKey) {
      params.set('date', dateKey);
      params.set('dateKey', dateKey);
    }
    params.set('_', Date.now().toString());
    return api.request(`/ai-analysis-published?${params.toString()}`);
  },
  getPrematchAnalysisCards: (dateKey = '') => {
    const params = new URLSearchParams();
    params.set('today', 'true');
    params.set('prematch', 'true');
    params.set('limit', '200');
    if (dateKey) {
      params.set('date', dateKey);
      params.set('dateKey', dateKey);
    }
    params.set('_', Date.now().toString());
    return api.request(`/analysis?${params.toString()}`);
  },
  getValueBets: () => api.request('/value-bets'),
  getValueBetSummary: () => api.request('/value-bets-published'),
  getValueBetsPublished: () => api.request('/value-bets-published'),
  getWinningArchive: (limit = 24, date = '') => api.request(`/analysis/winning-archive?limit=${limit}${date ? `&date=${date}` : ''}`),
  getWinningPremiumCoupons: (limit = 24, date = '') => api.request(`/coupons/winning-archive?limit=${limit}${date ? `&date=${date}` : ''}`),
  getRiskyPicks: () => api.request('/risky-picks-published'),
  getRiskyPicksPublished: () => api.request('/risky-picks-published'),
  createAnalysis: (matchId) => api.request('/analysis', {
    method: 'POST',
    body: JSON.stringify({ matchId })
  }),
  createBatchAnalyses: (matchIds) => api.request('/analysis/batch', {
    method: 'POST',
    body: JSON.stringify({ matchIds })
  }),
  approveAnalysis: (analysisId, isFreePreview = false) => api.request(`/analysis/${analysisId}/approve`, {
    method: 'PUT',
    body: JSON.stringify({ isFreePreview })
  }),

  // Coupons
  getPremiumCoupons: () => api.request('/coupons/premium'),
  getDailyPremiumCoupons: () => api.request('/coupons/premium'),
  getDailyPremiumCouponsPublished: () => api.request('/coupons/premium'),
  getLiveCouponStatus: () => api.request('/coupons/live/status'),
  getLiveCoupon: () => api.request('/coupons/live'),
  generatePremiumCoupons: () => api.request('/coupons/generate', {
    method: 'POST'
  }),
  createManualPremiumCoupon: (payload) => api.request('/coupons/manual', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),

  // Chatbot
  getSupportConfig: () => api.request('/chatbot/config'),
  sendSupportMessage: (payload) => api.request('/chatbot/message', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),
  saveSupportLead: (payload) => api.request('/chatbot/lead', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),

  // Admin
  getUsers: () => api.request('/admin/users'),
  getPremiumRequestCount: () => api.request('/admin/premium-requests/count'),
  getChatbotSummaries: () => api.request('/admin/chatbot/summaries'),
  getAnalysisBacktest: () => api.request('/admin/analysis-backtest'),
  queueAnalysisBacktest: () => api.request('/admin/analysis-backtest', {
    method: 'POST'
  }),
  getLatestAnalysisJob: () => api.request('/admin/latest-analysis-job'),
  getDailyBestPicksDebug: () => api.request('/admin/daily-best-picks-debug'),
  getDailyPicksDebug: () => api.request('/admin/daily-picks-debug'),
  getDailyDebug: () => api.request('/admin/daily-debug'),
  getPredictionPerformance: () => api.request('/admin/prediction-performance'),
  getAiAnalysisDebug: () => api.request('/admin/ai-analysis-debug'),
  triggerDailyRefresh: () => api.request('/admin/refresh-daily-analysis', {
    method: 'POST'
  }),
  generateAiAnalysisPublication: () => api.request('/admin/generate-ai-analysis-publication', {
    method: 'POST'
  }),
  runAiAnalysisNow: (dateKey, force = false) => api.request('/admin/run-ai-analysis-now', {
    method: 'POST',
    body: JSON.stringify({
      ...(dateKey ? { dateKey } : {}),
      force: Boolean(force)
    })
  }),
  runDailyAiJob: (dateKey, force = false) => api.request('/admin/run-daily-ai-job', {
    method: 'POST',
    body: JSON.stringify({
      ...(dateKey ? { dateKey } : {}),
      force: Boolean(force)
    })
  }),
  runPredictionSettlement: () => api.request('/admin/run-prediction-settlement', {
    method: 'POST'
  }),
  settlePredictions: () => api.request('/admin/settle-now', {
    method: 'POST'
  }),
  getAnalysisStatus: () => api.request('/admin/analysis-status'),
  getDailyRefreshStatus: () => api.request('/admin/daily-refresh-status'),
  generateMatchRadarPublication: () => api.request('/admin/generate-match-radar-publication', {
    method: 'POST'
  }),
  getLiveDisplaySettings: () => api.request('/admin/live-display'),
  updateLiveDisplaySettings: (payload) => api.request('/admin/live-display', {
    method: 'PUT',
    body: JSON.stringify(payload)
  }),
  updateChatbotSummaryStatus: (sessionId, status) => api.request(`/admin/chatbot/summaries/${sessionId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status })
  }),
  grantPremium: (userId, months = 1, days = null) => api.request(`/admin/users/${userId}/premium`, {
    method: 'PUT',
    body: JSON.stringify({
      months,
      ...(days ? { days } : {})
    })
  }),
  rejectPremiumRequest: (userId) => api.request(`/admin/users/${userId}/premium-request`, {
    method: 'PUT'
  }),
  rejectAccountDeletionRequest: (userId) => api.request(`/admin/users/${userId}/deletion-request`, {
    method: 'PUT'
  }),
  deleteUserAccount: (userId) => api.request(`/admin/users/${userId}/account`, {
    method: 'DELETE'
  }),
  revokePremium: (userId) => api.request(`/admin/users/${userId}/premium`, {
    method: 'DELETE'
  }),
  adminChangeUserPassword: (userId, newPassword) => api.request(`/admin/users/${userId}/password`, {
    method: 'PUT',
    body: JSON.stringify({ newPassword })
  })
};

export default api;
